import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Building2, MapPin, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

function AddSupplierModal({ onClose, onSaved, editData }) {
  const [form, setForm] = useState(editData || { name: '', short_code: '', contact_person: '', location: '', payment_terms: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const isEdit = !!editData?.supplier_id;

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Supplier name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/suppliers/${editData.supplier_id}`, form);
        toast.success('Supplier updated');
      } else {
        await api.post('/suppliers', form);
        toast.success('Supplier created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">{isEdit ? 'Edit' : 'Add'} Supplier</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {[
            { label: 'Name *', key: 'name', placeholder: 'Supplier name' },
            { label: 'Short Code', key: 'short_code', placeholder: 'e.g. SUP01' },
            { label: 'Contact Person', key: 'contact_person', placeholder: 'Name' },
            { label: 'Location', key: 'location', placeholder: 'City' },
            { label: 'Payment Terms', key: 'payment_terms', placeholder: 'e.g. Net 30' },
          ].map(({ label, key, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">{label}</label>
              <input type="text" value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                data-testid={`supplier-${key}`}
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
            </div>
          ))}
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              data-testid="supplier-notes"
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-supplier-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editSup, setEditSup] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch { toast.error('Failed to load suppliers'); }
    setLoading(false);
  };

  useEffect(() => { fetchSuppliers(); }, []);

  const filtered = suppliers.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.short_code?.toLowerCase().includes(search.toLowerCase()) ||
    s.location?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (sup) => {
    if (!window.confirm(`Delete supplier "${sup.name}"?`)) return;
    setDeleting(sup.supplier_id);
    try {
      await api.delete(`/suppliers/${sup.supplier_id}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">Suppliers</h2>
          <p className="text-slate-500 text-sm mt-0.5">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-supplier-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 shadow-sm">
          <Plus size={15} /> Add Supplier
        </button>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm p-3">
        <Search size={14} className="text-slate-400" />
        <input type="text" placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)}
          data-testid="suppliers-search"
          className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>
          Loading suppliers...
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          {search ? 'No suppliers match your search' : 'No suppliers yet. Add your first supplier.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(sup => (
            <div key={sup.supplier_id}
              className="bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-400 transition-colors cursor-pointer group"
              onClick={() => navigate(`/suppliers/${sup.supplier_id}`)}
              data-testid={`supplier-card-${sup.supplier_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center">
                    <Building2 size={16} className="text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{sup.name}</p>
                    {sup.short_code && <p className="text-xs text-slate-400 font-mono">{sup.short_code}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setEditSup(sup)} title="Edit" data-testid={`edit-supplier-${sup.supplier_id}`}
                    className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-sm"><Edit size={13} /></button>
                  <button onClick={() => handleDelete(sup)} disabled={deleting === sup.supplier_id} title="Delete"
                    data-testid={`delete-supplier-${sup.supplier_id}`}
                    className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm disabled:opacity-50"><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-slate-500">
                {sup.location && <p className="flex items-center gap-1.5"><MapPin size={11} /> {sup.location}</p>}
                {sup.contact_person && <p className="flex items-center gap-1.5"><FileText size={11} /> {sup.contact_person}</p>}
                {sup.payment_terms && <p className="text-slate-400">Terms: {sup.payment_terms}</p>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-slate-800">{fmt(sup.part_count || 0)}</span>
                <span className="text-[10px] text-slate-400 uppercase">Active Costings</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddSupplierModal onClose={() => setShowAdd(false)} onSaved={fetchSuppliers} />}
      {editSup && <AddSupplierModal editData={editSup} onClose={() => setEditSup(null)} onSaved={fetchSuppliers} />}
    </div>
  );
}
